import React, { createContext, useContext } from 'react';
import { Box, Button, Menu, MenuProps, Paper, PaperProps, Popover, PopoverProps, Typography, useMediaQuery, useTheme } from '@mui/material';
import VerticalOverflowScroller from './VerticalOverflowScroller';
import { dropdownScrollerSx } from './DropdownScrollPaper';

// Use a sheet only at phone widths. A narrow desktop pane or tablet should keep
// its menu anchored to the control that opened it.
export const useMobileMenu = () => useMediaQuery(useTheme().breakpoints.down('sm'));

// Keep MUI's selection, keyboard navigation, focus restoration, and dismissal.
// Only the menu's framing changes at the mobile breakpoint.
export const mobileMenuPaperSx = {
  position: 'absolute', boxSizing: 'border-box', outline: 0,
  top: 'auto !important', bottom: '0 !important', left: '0 !important', right: 0,
  width: '100% !important', minWidth: 0, maxWidth: '100% !important',
  maxHeight: '85dvh !important', m: '0 !important', p: '0 !important',
  borderRadius: '24px 24px 0 0 !important', bgcolor: '#101219', color: '#fff',
  border: '1px solid rgba(255,255,255,0.12)',
  transformOrigin: 'center bottom !important',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
} as const;

export const MobileMenuContext = createContext<{
  title?: React.ReactNode;
  close: (event: React.SyntheticEvent) => void;
  scrollContent?: boolean;
}>({ close: () => {} });

export const MobileMenuPaper = React.forwardRef<HTMLDivElement, PaperProps>(function MobileMenuPaper(
  { children, sx, ...props }, ref,
) {
  const { title, close, scrollContent = true } = useContext(MobileMenuContext);
  return <Paper {...props} ref={ref} data-mobile-sheet="true" sx={[
    ...(Array.isArray(sx) ? sx : [sx]), mobileMenuPaperSx,
  ]}>
    <Box aria-hidden="true" sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#B9BAC5', mx: 'auto', mt: 1.5, mb: 1, flexShrink: 0 }} />
    {title && <Typography component="h2" sx={{ px: 2.5, py: 1.5, m: 0, fontSize: 20, fontWeight: 750, flexShrink: 0 }}>{title}</Typography>}
    {scrollContent ? <VerticalOverflowScroller fadeHeight={24} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }} scrollerSx={dropdownScrollerSx}>
      {children}
    </VerticalOverflowScroller> : children}
    <Box sx={{ px: 2, pt: 1, pb: 'max(12px, env(safe-area-inset-bottom))', flexShrink: 0 }}>
      <Button fullWidth onClick={close} sx={{ color: '#fff', textTransform: 'none', minHeight: 44 }}>Close</Button>
    </Box>
  </Paper>;
});

type ResponsiveMenuProps = MenuProps & { sheetTitle?: React.ReactNode };

export default function ResponsiveMenu({ sheetTitle, ...props }: ResponsiveMenuProps) {
  const mobile = useMobileMenu();
  return <MobileMenuContext.Provider value={{ title: sheetTitle, close: event => props.onClose?.(event, 'escapeKeyDown') }}>
    <Menu {...props}
      anchorReference={mobile ? 'none' : props.anchorReference}
      slots={{ ...props.slots, ...(mobile ? { paper: MobileMenuPaper } : {}) }}
    />
  </MobileMenuContext.Provider>;
}

/** Pickers with tabs/preview controls keep their own scrolling interior. */
export function ResponsivePopover({ sheetTitle, ...props }: PopoverProps & { sheetTitle?: React.ReactNode }) {
  const mobile = useMobileMenu();
  return <MobileMenuContext.Provider value={{ title: sheetTitle, close: event => props.onClose?.(event, 'escapeKeyDown'), scrollContent: false }}>
    <Popover {...props}
      anchorReference={mobile ? 'none' : props.anchorReference}
      slots={{ ...props.slots, ...(mobile ? { paper: MobileMenuPaper } : {}) }}
    />
  </MobileMenuContext.Provider>;
}
